import {HttpBackend, HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {Observable, catchError, finalize, map, of, shareReplay, timeout} from 'rxjs';

import {ApiService} from './api.service';
import {EnvironmentService} from './environment.service';

interface HealthProbeResponse {
  status?: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class ApiProbeService {
  private apiService: ApiService = inject(ApiService);
  private environmentService: EnvironmentService = inject(EnvironmentService);
  private rawHttp: HttpClient = new HttpClient(inject(HttpBackend));

  private healthProbe$: Observable<boolean> | null = null;

  public probeCurrentApiHealth(): Observable<boolean> {
    if (!this.environmentService.isApiHealthProbeEnabled()) {
      return of(true);
    }

    if (this.healthProbe$) {
      return this.healthProbe$;
    }

    const apiUrl = this.environmentService.getApiUrl();
    this.apiService.setUrl(apiUrl);

    this.healthProbe$ = this.probeApiHealth(apiUrl).pipe(
      map((isHealthy) => {
        if (!isHealthy) {
          this.applyHealthProbeFallbackUrl();
        }

        return isHealthy;
      }),
      finalize(() => {
        this.healthProbe$ = null;
      }),
      shareReplay({bufferSize: 1, refCount: false})
    );

    return this.healthProbe$;
  }

  public shouldProbeApiHealthAfterServerError(): boolean {
    return this.environmentService.isApiHealthProbeEnabled() &&
      this.environmentService.isApiHealthProbeRetryUrl(this.apiService.getUrl());
  }

  public probeApiHealthAfterServerError(): void {
    if (!this.shouldProbeApiHealthAfterServerError()) {
      return;
    }

    this.probeCurrentApiHealth().subscribe();
  }

  private probeApiHealth(apiUrl: string): Observable<boolean> {
    const healthUrl = `${apiUrl}${this.environmentService.getApiHealthProbePath()}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Socket-ID': this.environmentService.getTabId() || ''
    });

    return this.rawHttp.get<HealthProbeResponse>(healthUrl, {headers}).pipe(
      timeout({first: this.environmentService.getApiHealthProbeTimeoutMs()}),
      map((response) => response?.status === this.environmentService.getApiHealthProbeExpectedStatus()),
      catchError(() => of(false))
    );
  }

  private applyHealthProbeFallbackUrl(): void {
    const fallbackUrl = this.environmentService.getApiHealthProbeFallbackUrl();

    if (!fallbackUrl || this.apiService.getUrl() === fallbackUrl) {
      return;
    }

    this.environmentService.setRuntimeApiUrl(fallbackUrl);
    this.apiService.setUrl(fallbackUrl);
  }
}
